import styled from '@emotion/styled';
import { css } from '@emotion/react';

import theme from 'styles/theme';

const shouldForwardProp = (prop: string) => !prop.startsWith('$');

export const TableSection = styled('div', { shouldForwardProp })<{ $compact?: boolean }>`
  padding: ${({ $compact }) => ($compact ? '0' : '0 16px')};
  overflow: visible;

  > div {
    overflow: visible;
  }

  table {
    border: none;
    table-layout: fixed;
    width: 100%;
    box-shadow: none;
  }

  ${({ $compact }) =>
    $compact &&
    css`
      padding-top: 8px;

      thead th:nth-of-type(2),
      tbody td:nth-of-type(2) {
        width: auto;
        text-align: left !important;
      }

      thead th:nth-of-type(3),
      tbody td:nth-of-type(3) {
        width: 112px;
        text-align: right !important;
        padding-right: 16px !important;
      }

      thead th,
      tbody td {
        padding-left: 8px !important;
        padding-right: 8px !important;
      }

      thead th:first-of-type,
      tbody td:first-of-type {
        width: 44px;
        padding-left: 16px !important;
        padding-right: 8px !important;
      }

      tbody td:nth-of-type(2) {
        font-family: ${theme.font.mangueira.bold}, 'Mangueira', sans-serif;
        font-size: 14px;
        font-weight: 700;
        line-height: 20px;
        text-align: left !important;
        color: ${theme.colors.primaryText};
      }
    `}

  ${({ $compact }) =>
    !$compact &&
    css`
      thead th:first-of-type,
      tbody td:first-of-type {
        width: 52px;
        padding-left: 4px !important;
        padding-right: 8px !important;
      }

      thead th:last-of-type,
      tbody td:last-of-type {
        width: 56px;
        padding-right: 4px !important;
        padding-left: 8px !important;
      }

      tbody td:nth-of-type(2),
      tbody td:nth-of-type(3),
      tbody td:nth-of-type(4),
      tbody td:nth-of-type(5),
      tbody td:nth-of-type(6) {
        font-family: ${theme.font.mangueira.bold}, 'Mangueira', sans-serif;
        font-size: 14px;
        font-weight: 700;
        font-style: normal;
        line-height: 20px;
        letter-spacing: 0;
        text-align: center;
        color: ${theme.colors.primaryText};
      }

      tbody td:nth-of-type(7) {
        text-align: center;
      }
    `}

  thead th {
    background: #fff !important;
    color: #575757 !important;
    border-top: none !important;
    border-bottom: ${({ $compact }) =>
      $compact ? '2px solid #e0e0e0' : '1px solid #e8e8e8'} !important;
    box-shadow: none;
    padding: ${({ $compact }) =>
      $compact ? '12px 8px' : '8px 12px'} !important;
    vertical-align: middle;
    text-align: center;
    font-family: ${theme.font.mangueira.bold}, 'Mangueira', sans-serif;
    font-size: 12px;
    font-weight: ${({ $compact }) => ($compact ? 600 : 400)};
    font-style: normal;
    line-height: 14px;
    letter-spacing: 0;
  }

  thead th > span {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    width: auto;
  }

  ${({ $compact }) =>
    $compact &&
    css`
      thead th:nth-of-type(2) > span {
        justify-content: flex-start;
      }

      thead th:nth-of-type(3) > span {
        justify-content: flex-end;
        width: 100%;
      }
    `}

  tbody td {
    padding: 12px 12px !important;
    border-bottom: none !important;
    vertical-align: middle;
  }

  ${({ $compact }) =>
    !$compact &&
    css`
      tbody tr:nth-of-type(even) {
        background: #fafafa;
      }

      tbody tr:nth-of-type(odd) {
        background: #fff;
      }
    `}

  ${({ $compact }) =>
    $compact &&
    css`
      tbody tr:nth-of-type(even),
      tbody tr:nth-of-type(odd) {
        background: #fff !important;
      }

      tbody tr[data-selected='true'] {
        background: #f2f2f2 !important;
      }

      tbody tr:last-of-type td {
        border-bottom: 2px solid #e0e0e0 !important;
      }
    `}
`;

export const CheckboxCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const StatusHeaderLabel = styled('span', { shouldForwardProp })<{
  $compact?: boolean;
}>`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  overflow: visible;

  > svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }

  ${({ $compact }) =>
    $compact &&
    css`
      justify-content: flex-end;
      width: 100%;
    `}
`;

export const BoletoNumberCell = styled('div', { shouldForwardProp })<{
  $compact?: boolean;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: ${({ $compact }) => ($compact ? 'flex-start' : 'center')};
  gap: 6px;
  max-width: 100%;
  width: ${({ $compact }) => ($compact ? '100%' : 'auto')};
`;

export const BoletoNumber = styled('span', { shouldForwardProp })<{
  $truncated?: boolean;
}>`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: ${({ $truncated }) => ($truncated ? 'help' : 'default')};
`;

const cellIconButtonStyles = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  padding: 0;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: #262626;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.7;
  }

  &:focus-visible {
    outline: 2px solid #262626;
    outline-offset: 2px;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

export const CopyButton = styled.button`
  ${cellIconButtonStyles}
`;

export const ViewButton = styled.button`
  ${cellIconButtonStyles}
`;

export const ActionsCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const ActionsMenuWrap = styled.div`
  ul[role='menu'] {
    min-width: 210px;
    padding: 16px 20px;
    border: none;
    border-radius: 12px;
    box-shadow: 0 8px 24px rgba(38, 38, 38, 0.12);
    z-index: 30;
  }

  li + li {
    margin-top: 16px;
  }

  button[role='menuitem'] {
    padding: 0;
    gap: 12px;
    font-family: 'Mangueira', sans-serif;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    color: #262626;
    background: transparent;

    &:hover:not(:disabled),
    &:focus-visible:not(:disabled) {
      background: transparent;
      opacity: 0.75;
    }

    &:disabled {
      opacity: 0.45;
      cursor: not-allowed;
    }

    svg {
      width: 24px;
      height: 24px;
      flex-shrink: 0;
    }
  }
`;

export const MenuTrigger = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: ${theme.colors.gray};
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;

  &:hover {
    background: #f5f5f5;
    color: ${theme.colors.primaryText};
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

export const EmptyMessage = styled.p`
  margin: 0;
  font-family: 'Mangueira', sans-serif;
  font-size: 16px;
  color: ${theme.colors.gray};
`;

type StatusBadgeProps = {
  $compact?: boolean;
  $backgroundColor: string;
  $borderColor: string;
  $color: string;
};

export const StatusBadge = styled('span', { shouldForwardProp })<StatusBadgeProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  box-sizing: border-box;
  min-width: ${({ $compact }) => ($compact ? '80px' : '128px')};
  max-width: ${({ $compact }) => ($compact ? '112px' : '256px')};
  height: ${({ $compact }) => ($compact ? '28px' : '28px')};
  padding: ${({ $compact }) => ($compact ? '6px 10px' : '8px 12px')};
  border: 1px solid ${({ $borderColor }) => $borderColor};
  border-radius: 999px;
  background-color: ${({ $backgroundColor }) => $backgroundColor};
  color: ${({ $color }) => $color};
  font-family: 'Mangueira', sans-serif;
  font-size: ${({ $compact }) => ($compact ? '12px' : '12px')};
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
`;
